import axios from 'axios';
import api from './api';
import { Base64 } from 'js-base64';
import qs from 'qs';
import { getRequestFallbacks, getStoredLanguage, persistLanguage } from '../i18n/language';

let feedbackRequestSequence = 0;

function emitRequestFeedback(name, detail) {
  if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

const instance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/gateway-api',
  timeout: 15000,
  // Authentication uses the token header. Cross-origin cookies are not needed
  // and would require Access-Control-Allow-Credentials on every API response.
  withCredentials: false,
});

instance.interceptors.request.use(
  (config) => {
    const headers = { ...config.headers };
    const currentLanguage = persistLanguage(getStoredLanguage());
    const backendLanguage = currentLanguage === 'zh-CN' ? 'zh-CN' : 'en';
    const token = localStorage.getItem('token');

    headers.lang = backendLanguage;
    headers['Accept-Language'] = backendLanguage === 'zh-CN' ? 'zh-CN' : 'en-US';

    if ((config.method === 'post' || config.method === 'put') && !(config.data instanceof FormData)) {
      headers['content-type'] = 'application/x-www-form-urlencoded;charset=UTF-8';
    }

    if (token) {
      headers.token = Base64.encode(token);
    }

    return {
      ...config,
      headers,
      params: {
        ...config.params,
        lang: backendLanguage,
      },
    };
  },
  (error) => Promise.reject(error)
);

function decodePayload(payload) {
  if (typeof payload !== 'string') {
    return payload;
  }

  const trimmed = payload.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const decoded = Base64.decode(trimmed);
    try {
      return JSON.parse(decoded);
    } catch (parseError) {
      return decoded;
    }
  } catch (decodeError) {
    return payload;
  }
}

function ensureLocalizedMessage(message, fallback) {
  const normalized = typeof message === 'string' ? message.trim() : '';
  if (getStoredLanguage() === 'zh-CN') return normalized || fallback;
  return normalized && !/[\u3400-\u9fff]/u.test(normalized) ? normalized : fallback;
}

function normalizeSuccessResponse(response) {
  const responseData = response && response.data;
  const body = responseData && typeof responseData === 'object'
    ? { ...responseData }
    : { data: responseData };
  const hasDataField = Object.prototype.hasOwnProperty.call(body, 'data');

  if (hasDataField) {
    body.data = decodePayload(body.data);
  }

  const numericCode = typeof body.code === 'undefined' ? undefined : Number(body.code);
  const ok = typeof numericCode === 'undefined' || numericCode === 0 || numericCode === 200;
  const fallback = getRequestFallbacks();
  const message = ensureLocalizedMessage(
    body.msg || body.message || '',
    ok ? fallback.success : fallback.requestFailed
  );

  const requestUrl = response && response.config && response.config.url;
  const isUserInfoAuthFailure = numericCode === 401 && /\/member\/getUserInfo$/i.test(requestUrl || '');
  if (isUserInfoAuthFailure) {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
  }

  return {
    ...body,
    ok,
    msg: message,
    message,
    raw: response,
    data: hasDataField ? body.data : body,
  };
}

function normalizeErrorResponse(error) {
  const response = error && error.response;
  const responseData = response && response.data;
  const body = responseData && typeof responseData === 'object'
    ? { ...responseData }
    : {};
  const isTimeout = error && (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || ''));
  const fallback = getRequestFallbacks();
  const fallbackMessage = isTimeout
    ? fallback.timeout
    : fallback.networkFailed;
  const message = ensureLocalizedMessage(
    body.msg || body.message || (error && error.message),
    fallbackMessage
  );
  const hasDataField = Object.prototype.hasOwnProperty.call(body, 'data');

  return {
    ok: false,
    code: typeof body.code !== 'undefined' ? body.code : ((response && response.status) || -1),
    msg: message,
    message,
    data: hasDataField ? decodePayload(body.data) : null,
    raw: error,
  };
}

async function http(option = {}) {
  const method = (option.method || 'get').toLowerCase();
  const requestData = option.data || option.params || {};
  const endpoint = api[option.url] || option.url;

  if (!endpoint) {
    return {
      ok: false,
      code: -1,
      msg: `Missing API configuration: ${option.url}`,
      message: `Missing API configuration: ${option.url}`,
      data: null,
      raw: null,
    };
  }

  const feedbackId = `request_${Date.now()}_${++feedbackRequestSequence}`;
  if (option.feedback !== false) {
    emitRequestFeedback('app-request-loading-start', {
      id: feedbackId,
      method,
      endpoint,
    });
  }

  try {
    let response;
    if (method === 'get' || method === 'delete') {
      response = await instance[method](endpoint, { params: requestData });
    } else {
      response = await instance[method](endpoint, requestData instanceof FormData ? requestData : qs.stringify(requestData));
    }

    return normalizeSuccessResponse(response);
  } catch (error) {
    const normalizedError = normalizeErrorResponse(error);
    if (option.feedback !== false) {
      emitRequestFeedback('app-request-loading-error', {
        id: feedbackId,
        message: normalizedError.message,
      });
    }
    return normalizedError;
  } finally {
    if (option.feedback !== false) {
      emitRequestFeedback('app-request-loading-end', { id: feedbackId });
    }
  }
}

export default http;
